import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  Tooltip,
  CircularProgress, // Import CircularProgress
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { H6, Small } from "components/Typography";
import toast from "react-hot-toast";
import { API_ENDPOINTS } from "Network/apiEndpoints"; // Import API_ENDPOINTS
import apiService from "Network/apiService"; // Import apiService
import ConfirmationDialog from "components/ConfirmationDialog";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "12px 16px",
  color: theme.palette.text.primary,
  "&.header": {
      fontWeight: 600,
      fontSize: "0.875rem",
  },
}));

const CategoryTable = ({ searchQuery, onEdit, refreshTrigger }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true); // State for loading indicator
  const [error, setError] = useState(null);     // State for error handling
  // Add states for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
      const fetchCategories = async () => {
          setLoading(true);
          setError(null);

          try {
              const response = await apiService.get(API_ENDPOINTS.GET_business_category_all_list);

              if (response?.success && response?.data) {
                  setCategories(response.data);
              } else {
                  setError(response?.message || "Failed to fetch categories");
              }
          } catch (error) {
              console.error("API error:", error);
              setError("An unexpected error occurred.");
          } finally {
              setLoading(false);
          }
      };

      fetchCategories();
  }, [refreshTrigger]);

  const handleDeleteClick = (category) => {
      setCategoryToDelete(category);
      setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
      if (!categoryToDelete) return;

      try {
          const response = await apiService.delete(
              `${API_ENDPOINTS.DELETE_business_category}?category_id=${categoryToDelete.id}`
          );

          if (response?.success) {
              setCategories(prevCategories =>
                  prevCategories.filter(category => category.id !== categoryToDelete.id)
              );
              toast.success("Category Deleted Successfully");
          } else {
              toast.error(response?.message || "Failed to delete category");
          }
      } catch (error) {
          console.error("Delete error:", error);
          toast.error("Failed to delete category");
      } finally {
          setDeleteDialogOpen(false);
          setCategoryToDelete(null);
      }
  };

  const handleDeleteCancel = () => {
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
  };

  const filteredCategories = categories ? categories.filter((category) =>
      category.category_name.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  if (loading) {
      return (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress />
          </Box>
      );
  }

  if (error) {
      return (
          <Box mt={2} color="error.main">
              Error: {error}
          </Box>
      );
  }

  return (
      <Box mt={2}>
          <TableContainer>
              <Table>
                  <TableHead>
                      <TableRow>
                          <StyledTableCell className="header">Category Name</StyledTableCell>
                          <StyledTableCell className="header">Description</StyledTableCell>
                          <StyledTableCell className="header">Status</StyledTableCell>
                          <StyledTableCell className="header" align="center">Actions</StyledTableCell>
                      </TableRow>
                  </TableHead>

                  <TableBody>
                      {categories && categories.length > 0 ? (
                          filteredCategories.map((category) => (
                              <TableRow key={category.id}>
                                  <StyledTableCell>
                                      <H6>{category.category_name}</H6>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                      <Small>{category.description}</Small>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                      <Small
                                          sx={{
                                              color: category.status === 'active' ? 'success.main' : 'error.main',
                                              backgroundColor: category.status === 'active' ? 'success.light' : 'error.light',
                                              padding: '3px 12px',
                                              borderRadius: '12px',
                                              display: 'inline-block'
                                          }}
                                      >
                                          {category.status}
                                      </Small>
                                  </StyledTableCell>
                                  <StyledTableCell align="center">
                                      <Tooltip title="Edit Category">
                                          <IconButton
                                              size="small"
                                              onClick={() => onEdit(category)}
                                          >
                                              <Edit />
                                          </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete Category">
                                          <IconButton
                                              size="small"
                                              onClick={() => handleDeleteClick(category)}
                                          >
                                              <Delete />
                                          </IconButton>
                                      </Tooltip>
                                  </StyledTableCell>
                              </TableRow>
                          ))
                      ) : (
                          <TableRow>
                              <TableCell colSpan={4} align="center">
                                  No categories available.
                              </TableCell>
                          </TableRow>
                      )}
                  </TableBody>
              </Table>
          </TableContainer>

          {/* Add Confirmation Dialog */}
          <ConfirmationDialog
              open={deleteDialogOpen}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              title="Delete Category"
              message={`Are you sure you want to delete category "${categoryToDelete?.category_name}"?`}
              confirmButtonText="Delete"
              confirmButtonColor="error"
              isLoading={loading}
          />
      </Box>
  );
};

export default CategoryTable;
